export class ApiError extends Error {
  constructor(message, status, code, correlationId = '') {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.correlationId = correlationId
  }
}

const stableErrorMessageKeys = {
  username_exists: 'accessUsernameExists',
  role_name_exists: 'accessRoleNameExists',
  role_assigned: 'roleAssignedError',
  last_administrator_required: 'accessLastAdministratorRequired',
  self_user_management_forbidden: 'currentAccountProtected',
  system_administrator_required: 'accessAdministratorRequired',
  permission_denied: 'accessPermissionDenied',
  system_role_immutable: 'systemRoleImmutableHint',
  high_risk_confirmation_required: 'highRiskHint',
}

export function apiErrorMessage(t, error, fallbackKey = 'accessRequestFailed') {
  const key = stableErrorMessageKeys[error?.code]
  if (key) return t(key)
  return error instanceof ApiError ? t(fallbackKey) : (error?.message || t(fallbackKey))
}

export const sessionRevokedEvent = 'next-reports:session-revoked'
let authenticationEpoch = 0

export function invalidatePreviousSessionResponses() {
  authenticationEpoch++
}

async function request(path, options = {}) {
  const requestAuthenticationEpoch = authenticationEpoch
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    },
    ...options,
  })

  if (!response.ok) {
    const payload = await response.json().catch(() => null)
    if (response.status === 401
      && requestAuthenticationEpoch === authenticationEpoch
      && path !== '/auth/login'
      && path !== '/auth/me'
      && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(sessionRevokedEvent))
    }
    throw new ApiError(payload?.error?.message ?? 'Request failed', response.status,
      payload?.error?.code, payload?.correlationId)
  }
  return response
}

export async function api(path, options = {}) {
  return (await request(path, options)).json().catch(() => null)
}

export async function apiBinary(path, options = {}) {
  const response = await request(path, options)
  return { blob: await response.blob(), disposition: response.headers.get('Content-Disposition') || '' }
}

export const getCurrentIndicators = (options = {}) => api('/online-indicators/current', options)

function requireHistoricalStation(stationId, payload) {
  if (Number(payload?.station?.id) !== Number(stationId)) {
    throw new ApiError(
      'Відповідь історії належить іншій АЗС.',
      502,
      'historical_station_mismatch',
    )
  }
  return payload
}

export const getHistoricalShifts = (stationId, { cursor = '', limit = 50, signal } = {}) => {
  const query = new URLSearchParams({ limit: String(limit) })
  if (cursor) query.set('cursor', cursor)
  return api(`/stations/${stationId}/online-indicators/history/shifts?${query}`, { signal })
    .then((payload) => requireHistoricalStation(stationId, payload))
}
export const getHistoricalIndicators = (stationId, session, options = {}) => api(
  `/stations/${stationId}/online-indicators/history/${session}`, options,
).then((payload) => requireHistoricalStation(stationId, payload))
export const getHistoricalTankSeries = (stationId, session, tankNumber, options = {}) => api(
  `/stations/${stationId}/online-indicators/history/${session}/tanks/${tankNumber}`, options,
).then((payload) => requireHistoricalStation(stationId, payload))
export const getTankCapacities = (stationId) => api(`/admin/stations/${stationId}/tank-capacities`)
export const saveTankCapacity = (stationId, tankNumber, capacityLiters) => api(
  `/admin/stations/${stationId}/tank-capacities/${tankNumber}`,
  { method: 'PUT', body: JSON.stringify({ capacityLiters }) },
)
export const deleteTankCapacity = (stationId, tankNumber) => api(
  `/admin/stations/${stationId}/tank-capacities/${tankNumber}`,
  { method: 'DELETE' },
)
export const getAutomationSettings = () => api('/admin/automation')
export const saveAutomationSettings = (stationId, enabled, intervalMinutes, version) => api(
  `/admin/automation/${stationId}`,
  { method: 'PUT', body: JSON.stringify({ enabled, intervalMinutes, version }) },
)
export const getMaintenanceOverview = (stationId) => api(`/admin/automation/${stationId}/maintenance`)
export const getMaintenanceShifts = (stationId, { page = 1, limit = 25, search = '' } = {}) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (search) query.set('search', search)
  return api(`/admin/automation/${stationId}/maintenance/shifts?${query}`)
}
export const getMaintenanceRereadShifts = (stationId, { page = 1, limit = 25, search = '' } = {}) => {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (search) query.set('search', search)
  return api(`/admin/automation/${stationId}/maintenance/reread-shifts?${query}`)
}
export const previewMaintenance = (stationId, mode, throughSession, fromSession, stateVersion) => api(
  `/admin/automation/${stationId}/maintenance/preview`,
  { method: 'POST', body: JSON.stringify({ mode, throughSession, fromSession, stateVersion }) },
)
export const executeMaintenance = (stationId, operationId, previewToken, confirmationText,
  backupAcknowledged) => api(`/admin/automation/${stationId}/maintenance/execute`, {
  method: 'POST',
  body: JSON.stringify({ operationId, previewToken, confirmationText, backupAcknowledged }),
}).then((response) => response.operation)
export const getMaintenanceOperation = (stationId, operationId) => api(
  `/admin/automation/${stationId}/maintenance/operations/${operationId}`,
)
export const resumeMaintenance = (stationId, operationId) => api(
  `/admin/automation/${stationId}/maintenance/operations/${operationId}/resume`,
  { method: 'POST' },
).then((response) => response.operation)
export const retryMaintenanceBackfill = (stationId, operationId) => api(
  `/admin/automation/${stationId}/maintenance/operations/${operationId}/retry-backfill`,
  { method: 'POST' },
).then((response) => response.operation)
export const getReceipts = (stationId, {
  page = 1,
  cursor = '',
  limit = 25,
  search = '',
  from = '',
  to = '',
  types = [],
  paymentFormIds = [],
} = {}) => {
  const query = new URLSearchParams({ limit: String(limit) })
  if (cursor) query.set('cursor', cursor)
  else query.set('page', String(page))
  if (search) query.set('search', search)
  if (from) query.set('from', from)
  if (to) query.set('to', to)
  types.forEach((value) => query.append('type', value))
  paymentFormIds.forEach((value) => query.append('paymentFormId', String(value)))
  return api(`/stations/${stationId}/receipts?${query}`)
}
export const getReceiptFilterOptions = (stationId) => api(`/stations/${stationId}/receipts/filter-options`)
export const getReceipt = (stationId, saleTag) => api(`/stations/${stationId}/receipts/${saleTag}`)

export const getAccessUsers = ({ search = '', roleId = '', status = '', page = 0, pageSize = 25 } = {}) => {
  const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
  if (search) query.set('search', search)
  if (roleId) query.set('roleId', String(roleId))
  if (status) query.set('status', status)
  return api(`/access/users?${query}`)
}
export const getAccessUser = (userId) => api(`/access/users/${userId}`)
export const createAccessUser = (payload) => api('/access/users', {
  method: 'POST', body: JSON.stringify(payload),
})
export const updateAccessUser = (userId, payload) => api(`/access/users/${userId}`, {
  method: 'PUT', body: JSON.stringify(payload),
})
export const updateAccessUserStatus = (userId, enabled, version) => api(`/access/users/${userId}/status`, {
  method: 'PUT', body: JSON.stringify({ enabled, version }),
})
export const deleteAccessUser = (userId, version) => api(`/access/users/${userId}`, {
  method: 'DELETE', headers: { 'If-Match-Version': String(version) },
})
export const getAccessPermissions = () => api('/access/permissions')
export const getAccessRoles = ({ search = '', page = 0, pageSize = 25 } = {}) => {
  const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
  if (search) query.set('search', search)
  return api(`/access/roles?${query}`)
}
export const getAccessRole = (roleId) => api(`/access/roles/${roleId}`)
export const createAccessRole = (payload) => api('/access/roles', {
  method: 'POST', body: JSON.stringify(payload),
})
export const updateAccessRole = (roleId, payload) => api(`/access/roles/${roleId}`, {
  method: 'PUT', body: JSON.stringify(payload),
})
export const deleteAccessRole = (roleId, version) => api(`/access/roles/${roleId}`, {
  method: 'DELETE', headers: { 'If-Match-Version': String(version) },
})

export const getApplicationCatalog = () => api('/platform/applications')
export const getPlatformContext = () => api('/platform/context')
export const selectPhysicalStation = (stationId) => api('/platform/context/station', {
  method: 'PUT', body: JSON.stringify({ stationId }),
})
export const selectNaftaPosSource = (sourceInstanceId) => api('/naftapos/context/source', {
  method: 'PUT', body: JSON.stringify({ sourceInstanceId }),
})

export const getPts2Context = () => api('/pts2/context')
export const selectPts2Controller = (controllerId) => api('/pts2/context/controller', {
  method: 'PUT', body: JSON.stringify({ controllerId }),
})
export const getPts2CurrentStatus = (controllerId, options = {}) => api(
  `/pts2/controllers/${controllerId}/status`, options,
)
export const requestPts2CurrentStatusRefresh = (controllerId) => api(
  `/pts2/controllers/${controllerId}/status/refresh`, { method: 'POST' },
)
export const getPhysicalStations = () => api('/platform/stations')
export const getPhysicalStation = (stationId) => api(`/platform/stations/${stationId}`)
export const createPhysicalStation = (payload) => api('/platform/stations', {
  method: 'POST', body: JSON.stringify(payload),
})
export const updatePhysicalStation = (stationId, payload) => api(`/platform/stations/${stationId}`, {
  method: 'PUT', body: JSON.stringify(payload),
})
export const updatePhysicalStationStatus = (stationId, status, version) => api(
  `/platform/stations/${stationId}/status`,
  { method: 'PUT', body: JSON.stringify({ status, version }) },
)
export const deletePhysicalStation = (stationId, version) => api(`/platform/stations/${stationId}`, {
  method: 'DELETE', headers: { 'If-Match-Version': String(version) },
})

export const createNaftaPosInstance = (stationId, payload) => api(
  `/platform/stations/${stationId}/naftapos-instances`,
  { method: 'POST', body: JSON.stringify(payload) },
)
export const updateNaftaPosInstance = (stationId, sourceInstanceId, payload) => api(
  `/platform/stations/${stationId}/naftapos-instances/${sourceInstanceId}`,
  { method: 'PUT', body: JSON.stringify(payload) },
)
export const deleteNaftaPosInstance = (stationId, sourceInstanceId, version) => api(
  `/platform/stations/${stationId}/naftapos-instances/${sourceInstanceId}`,
  { method: 'DELETE', headers: { 'If-Match-Version': String(version) } },
)
export const testNaftaPosInstance = (stationId, sourceInstanceId) => api(
  `/platform/stations/${stationId}/naftapos-instances/${sourceInstanceId}/test`,
  { method: 'POST' },
)
